// chat/ChatCard.tsx

import Avatar from "../../../design-system/Avatar";
import { RenderResponse } from "./RenderResponse";
import { RenderCitations } from "./RenderCitations";
import RenderFollowupQuestions from "./RenderFollowupQuestions"; // Make sure this import is correct
import { RelatedCaseletProps } from "./RenderFollowupCaselet";
import { HiHandThumbDown, HiHandThumbUp, HiOutlineHandThumbDown, HiOutlineHandThumbUp } from "react-icons/hi2";
import { useEffect, useRef, useState } from "react";
import { Tooltip } from "@material-tailwind/react";
import { ChatFeedBack } from "../../../api/axiosInstanceApi";

export type ChatCardProps = {
  id?: string;
  index?: number;
  feedBack?: string;
  prompt?: string;
  response?: null | string | string[] | JSX.Element[] | JSON | React.MouseEventHandler;
  citations?: Array<{ citation: string; link: string; title: string; author: string; book_name?: string }>;
  isResponseEmptyFlag?: boolean;
  isLoading?: boolean;
  responseForFineTune?: string;
  conversation_id?: string;
  followupQuestions?: string[];
  relatedCaselet?: RelatedCaseletProps[];
  onCitationClick?: (citation: any) => void;
  onFollowupQuestionClicked?: (question: string) => void;
  activeFollowupQuestionIndex?: number | null;
  isOldInteractions?: boolean;
  disableFollowupQuestions?: boolean; // This prop is correctly defined here
};

const ChatCard: React.FC<ChatCardProps> = ({
  prompt,
  index,
  response,
  citations,
  feedBack,
  followupQuestions,
  isResponseEmptyFlag,
  conversation_id,
  onCitationClick,
  isLoading,
  onFollowupQuestionClicked,
  isOldInteractions,
  disableFollowupQuestions // Make sure to destructure it
}: ChatCardProps) => {
  // console.log("isLoading in chatCard: ", disableFollowupQuestions); // This console log will now show the correct value
  const [isThumbUpClicked, setIsThumbUpClicked] = useState<Boolean>(false);
  const [isThumbDownClicked, setIsThumbDownClicked] = useState<Boolean>(false);

  const getInitials = (name: any) => {
    return name
      .split(" ")
      .map((n: any) => n[0])
      .join("");
  };

  useEffect(() => {
    if (feedBack === "bad") {
      setIsThumbDownClicked(true);
    }
    if (feedBack === "good") {
      setIsThumbUpClicked(true);
    }
  }, [feedBack]); // Add feedBack to dependency array for useEffect

  const isCitationsEmpty =
    !citations ||
    (Array.isArray(citations) && citations.length === 0) ||
    (typeof citations === "object" && !Array.isArray(citations) && Object.keys(citations).length === 0);
  let menteeData: any = localStorage.getItem("mentee_data");
  if (menteeData) {
    try {
      menteeData = JSON.parse(menteeData);
    } catch (e) {
      console.error("Error parsing mentee_data from localStorage:", e);
      menteeData = null;
    }
  } else {
    menteeData = null;
  }
  const menteeName = menteeData ? menteeData.name : "John Doe";
  const handleThumbsUp = async () => {
    if (!isThumbUpClicked) {
      const res = await ChatFeedBack(conversation_id, "good", index);
      const resJson = res.data;
      if (resJson.status !== "failed") {
        setIsThumbUpClicked(true); // Set to true, not toggle
        setIsThumbDownClicked(false);
      }
    } else {
      const res = await ChatFeedBack(conversation_id, "", index);
      const resJson = res.data;

      if (resJson.status !== "failed") {
        setIsThumbUpClicked(false); // Set to false, not toggle
      }
    }
  };
  const handleThumbsDown = async () => {
    if (!isThumbDownClicked) {
      const res = await ChatFeedBack(conversation_id, "bad", index);
      const resJson = res.data;
      if (resJson.status !== "failed") {
        setIsThumbUpClicked(false);
        setIsThumbDownClicked(true); // Set to true, not toggle
      }
    } else {
      const res = await ChatFeedBack(conversation_id, "", index);
      const resJson = res.data;
      if (resJson.status !== "failed") {
        setIsThumbUpClicked(false);
        setIsThumbDownClicked(false); // Set to false, not toggle
      }
    }
  };
  const lastCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (lastCardRef.current) {
      const elementRect = lastCardRef.current.getBoundingClientRect();
      const headerHeight = 60; // Adjust based on your fixed header height

      window.scrollTo({
        top: elementRect.top + window.pageYOffset - headerHeight,
        behavior: "smooth"
      });
    }
  }, [isLoading]);

  return (
    <div className="">
      {isOldInteractions && <div ref={lastCardRef} />}

      <div className="flex items-center p-2 ">
        <div className="max-w-14">
          <Avatar initials={getInitials(menteeName)} className="mr-4 shadow-lg " />
        </div>

        <div>
          <p className="text-lg font-semibold card_text_color ">{prompt}</p>
        </div>
      </div>
      <div className="">
        <div className=" min-w-40">
          <div className="p-4 rounded-lg shadow-lg card_text_color card_bg_color">
            <div className="">
              <RenderResponse isLoading={isLoading} response={response} />
              {!isCitationsEmpty && <div className="mt-5"></div>}
              <div className="pl-2 grid w-full">
                <RenderCitations isLoading={isLoading} citations={citations} onCitationClick={onCitationClick} />
                {!isLoading && citations && !isResponseEmptyFlag && (
                  <div className=" text-end w-full">
                    <div className="flex justify-end space-x-6 mt-4 ">
                      {isThumbUpClicked ? (
                        // clicked
                        <Tooltip
                          content="Useful"
                          placement="top"
                          className="text-white bg-transparent border-0   border-slate-100 font-bold "
                        >
                          <button onClick={handleThumbsUp} className=" text-amber-400  cursor-pointer">
                            <HiHandThumbUp size={26} />
                          </button>
                        </Tooltip>
                      ) : (
                        //not clicked
                        <Tooltip content="Useful" placement="top" className="text-white bg-transparent font-bold">
                          <button onClick={handleThumbsUp} className=" text-amber-400  cursor-pointer">
                            <HiOutlineHandThumbUp size={26} />
                          </button>
                        </Tooltip>
                      )}

                      {isThumbDownClicked ? (
                        // clicked
                        <Tooltip content="Not Useful" placement="top" className="text-white bg-transparent font-bold">
                          <button onClick={handleThumbsDown} className=" text-amber-400  cursor-pointer">
                            <HiHandThumbDown size={26} />
                          </button>
                        </Tooltip>
                      ) : (
                        //not clicked
                        <Tooltip content="Not Useful" placement="top" className="text-white bg-transparent font-bold ">
                          <button onClick={handleThumbsDown} className=" text-amber-400  cursor-pointer">
                            <HiOutlineHandThumbDown size={26} />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {isResponseEmptyFlag && conversation_id && prompt && <p>Oops, something went wrong </p>}
            </div>
          </div>
          {!isResponseEmptyFlag ? (
            <div className="flex">
              {followupQuestions?.length ? (
                <div className="w-2/2">
                  <RenderFollowupQuestions
                    isLoading={isLoading}
                    followupQuestions={followupQuestions}
                    onFollowupQuestionClicked={onFollowupQuestionClicked}
                    isDisabled={disableFollowupQuestions} // <-- Pass the prop here!
                  />
                </div>
              ) : (
                ""
              )}
            </div>
          ) : (
            ""
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatCard;
